// ── 입력 영역 ─────────────────────────────────────
// 첨부 미리보기 + 텍스트 입력(드래그&드롭·붙여넣기) + 컨트롤 바(첨부/드라이브/프롬프트/모델/
// 웹검색·사내데이터·문서기반답변 토글/전송·중지) + 안내 푸터.
// 순수 프레젠테이션: 모든 상태·핸들러·ref·상수는 ChatPage에서 props로 주입(동작 100% 동일).
import { useEffect, useRef, useState } from 'react';
import { supportsReasoning } from '../../utils/modelCaps';
import { useNavigate } from 'react-router-dom';
import { IconBook2, IconBrain, IconBuilding, IconFileText, IconFolder, IconLock, IconPaperclip, IconWorld } from '@tabler/icons-react';

const formatPendingFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return '';
  const unit = size < 1024 * 1024 ? 'KB' : 'MB';
  const value = unit === 'KB' ? size / 1024 : size / (1024 * 1024);
  return `${value.toFixed(1)}${unit}`;
};

const formatPendingFileLabel = (file) => {
  const sizeLabel = formatPendingFileSize(file?.size ?? file?.file_size ?? file?.fileSize ?? file?.bytes ?? file?.file?.size);
  return sizeLabel ? `${file.name} (${sizeLabel})` : file.name;
};

export default function ChatInput({
  mobile,
  keyboardOpen = false,
  scrollToBottom,
  pendingFiles, setPendingFiles, MAX_FILES,
  pendingFolders = [], removeDriveFolder,
  textareaRef, input, setInput, handleSend, handlePaste,
  fileRef, handleFileChange, driveAccess, setShowDrivePicker, openPrompts,
  models, selectedChatModel, setSelectedChatModel,
  webSearch, setWebSearch,
  reasoning, setReasoning,
  dbAccess, internalLan, internalData, setInternalData,
  useRag, setUseRag, ragAccess = false,
  loading, stopResponse, draftKey,
}) {
  const navigate = useNavigate();
  const canSend = !loading && (input.trim() || pendingFiles.length > 0 || pendingFolders.length > 0);
  const restoringDraftRef = useRef(false);
  const focusScrollTimerRef = useRef(null);
  const [toolPopoverOpen, setToolPopoverOpen] = useState(false);
  const toolsButtonRef = useRef(null);
  const toolsPopoverRef = useRef(null);
  // 컨트롤 바의 **CSS 폭**을 직접 잰다. 모바일 확대(html zoom)가 올라갈수록 CSS 폭이 줄어
  // (360px 화면 @250% → 144px) 토글이 4줄로 접히고, 카드가 하단 여백을 넘어 전송이 탭바에 가렸다.
  // 좁아지면 토글을 아이콘만 남겨 2줄 안에 들어오게 한다(2026-08-21 기기×배율 실측).
  const barRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [ultraNarrow, setUltraNarrow] = useState(false);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect?.width || 999;
      setNarrow(w < 250);
      setUltraNarrow(w < 200);      // 배율을 크게 올린 경우 — 토글을 '＋(도구)' 안으로 옮긴다
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const reasoningAvailable = supportsReasoning(models, selectedChatModel);
  const reasoningActive = !!reasoning && reasoningAvailable;
  const userDraftScope = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return String(u.id || u.email || 'anon');
    } catch (e) {
      return 'anon';
    }
  })();
  const draftStorageKey = `chatDraft:${userDraftScope}:${draftKey || 'new'}`;

  useEffect(() => {
    restoringDraftRef.current = true;
    const saved = (() => { try { return sessionStorage.getItem(draftStorageKey) || ''; } catch (e) { return ''; } })();
    setInput(saved);
    setTimeout(() => {
      restoringDraftRef.current = false;
      try {
        const el = textareaRef.current;
        if (el) {
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, mobile ? 112 : 126) + 'px';
        }
      } catch (e) { /* */ }
    }, 0);
  }, [draftStorageKey]);

  useEffect(() => {
    if (restoringDraftRef.current) return;
    try {
      const value = (input || '').slice(0, 4000);
      if (value && value.trim()) sessionStorage.setItem(draftStorageKey, value);
      else sessionStorage.removeItem(draftStorageKey);
    } catch (e) { /* */ }
  }, [draftStorageKey, input]);

  useEffect(() => {
    if (!toolPopoverOpen) return undefined;
    const closeIfOutside = (e) => {
      if (toolsButtonRef.current?.contains(e.target) || toolsPopoverRef.current?.contains(e.target)) return;
      setToolPopoverOpen(false);
    };
    const closeOnEsc = (e) => {
      if (e.key === 'Escape') setToolPopoverOpen(false);
    };
    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside);
    document.addEventListener('keydown', closeOnEsc);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('keydown', closeOnEsc);
    };
  }, [toolPopoverOpen]);

  useEffect(() => () => {
    if (focusScrollTimerRef.current !== null) clearTimeout(focusScrollTimerRef.current);
  }, []);

  const pillButtonStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, lineHeight: 1.4,
    background: active ? 'var(--accent)' : 'transparent',
    border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
    borderRadius: '16px', padding: '5px 11px', cursor: 'pointer',
    color: active ? 'white' : 'var(--text-muted)', fontSize: '11px', fontWeight: '500', minHeight: '32px', maxWidth: '100%',
  });
  const statusBadgeStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    fontWeight: '700', padding: '0 6px', borderRadius: '8px', fontSize: '11px',
    background: active ? 'var(--accent)' : 'var(--accent-weak)',
    color: active ? 'white' : 'var(--selected-text)',
    whiteSpace: 'nowrap',
  });
  const closeTools = () => setToolPopoverOpen(false);
  const requestPermission = () => {
    closeTools();
    navigate('/inquiries');
  };
  const openDrivePicker = () => {
    if (pendingFiles.length >= MAX_FILES) return;
    closeTools();
    setShowDrivePicker(true);
  };
  const openPromptLibrary = () => {
    closeTools();
    openPrompts();
  };
  const handleComposerFocus = () => {
    if (!mobile) return;
    // 키보드 전환 레이아웃이 끝난 뒤 메시지 끝을 가시 영역으로 되돌린다(e2e 회귀 검증).
    if (focusScrollTimerRef.current !== null) clearTimeout(focusScrollTimerRef.current);
    focusScrollTimerRef.current = setTimeout(() => {
      focusScrollTimerRef.current = null;
      scrollToBottom?.();
    }, 300);
  };
  const toolRowStyle = (active, disabled) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    background: active ? 'var(--accent-weak)' : 'white',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    textAlign: 'left',
  });
  const renderToolRow = ({ key, icon, label, description, active = false, disabled = false, status, onClick }) => (
    <button key={key} type="button" className="chat-tool-row" onClick={disabled ? undefined : onClick} disabled={disabled}
      style={toolRowStyle(active, disabled)}>
      <span aria-hidden="true" style={{ width: '24px', height: '24px', borderRadius: '8px', background: active ? 'var(--accent)' : '#f3f4f6', color: active ? 'white' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '13px' }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 700, lineHeight: 1.35 }}>{label}</span>
        <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.35, marginTop: '2px' }}>{description}</span>
      </span>
      <span style={statusBadgeStyle(active)}>{status}</span>
    </button>
  );

  return (
    <>
      {/* 입력창 그림자는 여기(클래스 규칙)에만 둔다 — inline boxShadow 는 :focus-within 포커스 링을 늘 이겨 링이 안 보인다. 첨부·도구 단추(.chat-icon-btn) 바탕·글자색도 같은 이유로 여기서만 준다(인라인은 hover 를 이긴다). */}
      <style>{`.chat-composer-card{box-shadow:var(--gw-float-shadow, 0 4px 24px #1c191711, 0 1px 3px #1c19170b)}.chat-composer-card:focus-within{box-shadow:0 0 0 3px var(--gw-focus, #2383e24d),0 4px 24px #1c191711}.chat-tool-row:hover:not(:disabled){background:var(--gw-hover, #efefed)}.chat-icon-btn{background:transparent;color:var(--text-muted)}.chat-icon-btn:hover{background:var(--gw-hover, #efefed);color:var(--gw-text, #32312e)}`}</style>
      {/* 첨부 미리보기 — 드라이브 폴더 칩 먼저, 그다음 파일 칩 */}
      {(pendingFolders.length > 0 || pendingFiles.length > 0) && (
        <div style={{ padding: mobile ? '8px 0 0' : '8px 0 0' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: mobile ? '0 12px' : '0 20px', display: 'flex', gap: '8px', flexWrap: 'wrap', boxSizing: 'border-box' }}>
          {pendingFolders.map((f) => (
            <div key={`folder:${f.path}`} title={`드라이브 ${f.path} — 하위 폴더까지 AI가 읽습니다`}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'var(--accent-weak)', border: '1px solid var(--border)', borderRadius: '20px', fontSize: '13px', color: 'var(--text)', maxWidth: mobile ? 'calc(100vw - 44px)' : '260px', minWidth: 0 }}>
              <IconFolder size={16} stroke={1.8} aria-hidden="true" style={{ flexShrink: 0 }} />
              <span style={{ maxWidth: mobile ? 'calc(100vw - 126px)' : '180px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${f.name} · 파일 ${f.file_count}${f.truncated ? '+' : ''}`}</span>
              <button type="button" aria-label={`${f.name} 폴더 첨부 취소`} onClick={() => removeDriveFolder?.(f.path)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1, alignSelf: 'flex-start' }}>×</button>
            </div>
          ))}
          {pendingFiles.map((f) => (
            <div key={f.file_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: f.preview ? '4px 10px 4px 4px' : '4px 10px', background: 'var(--accent-weak)', border: '1px solid var(--border)', borderRadius: f.preview ? '10px' : '20px', fontSize: '13px', color: 'var(--text)', maxWidth: mobile ? 'calc(100vw - 44px)' : '260px', minWidth: 0 }}>
              {f.preview
                ? <img src={f.preview} alt={f.name} onClick={() => window.open(f.preview, '_blank')}
                    title="클릭하면 원본 크기로 봅니다"
                    style={{ width: '46px', height: '46px', objectFit: 'cover', borderRadius: '8px', display: 'block', cursor: 'zoom-in', border: '1px solid #dbeafe' }} />
                : <IconPaperclip size={16} stroke={1.8} aria-hidden="true" />}
              <span style={{ maxWidth: mobile ? 'calc(100vw - 126px)' : '180px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatPendingFileLabel(f)}</span>
              <button onClick={() => { if (f.preview) URL.revokeObjectURL(f.preview); setPendingFiles((prev) => prev.filter((p) => p.file_id !== f.file_id)); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1, alignSelf: 'flex-start' }}>×</button>
            </div>
          ))}
          </div>
        </div>
      )}

      {/* 입력 영역 */}
      <div style={{ padding: mobile ? (keyboardOpen ? '8px 0 8px' : '12px 0 calc(12px + env(safe-area-inset-bottom))') : '18px 0 20px', background: 'transparent', borderTop: 'none' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: mobile ? '0 12px' : '0 20px', boxSizing: 'border-box' }}>
        <div
          className="chat-composer-card"
          style={{ background: 'var(--surface)', borderRadius: '13px', border: '1px solid var(--gw-border, #e5e5e2)', padding: mobile ? '11px 10px 9px' : '13px 14px 10px', transition: 'box-shadow .15s, background .15s' }}>
          {/* 텍스트 입력 */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, mobile ? 112 : 126) + 'px';
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            onPaste={handlePaste}
            onFocus={handleComposerFocus}
            placeholder="무엇이든 물어보세요"
            rows={1}
            style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', resize: 'none', outline: 'none', fontSize: '13px', lineHeight: '1.6', minHeight: '26px', maxHeight: mobile ? '112px' : '126px', overflowY: 'auto', fontFamily: 'inherit', display: 'block', overflowWrap: 'break-word', color: 'var(--text)' }}
          />
          {/* 컨트롤 바 — flexWrap: 좁은 폭·모바일 zoom(1.5×)에서 토글/전송이 한 줄을 넘으면 다음 줄로 접혀
              우측 버튼(사내데이터·문서기반답변·전송)이 화면 밖으로 잘리지 않게 함(데스크톱 넓은 폭=한 줄 유지). */}
          <div ref={barRef} style={{ display: 'flex', alignItems: 'center', gap: '6px', rowGap: '8px', marginTop: '8px', flexWrap: 'wrap', minWidth: 0 }}>
            <button className="chat-icon-btn" onClick={() => pendingFiles.length < MAX_FILES && fileRef.current?.click()}
              style={{ border: 'none', cursor: pendingFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                color: pendingFiles.length >= MAX_FILES ? 'var(--gw-muted, #92918d)' : undefined, padding: '4px', fontSize: '18px', flexShrink: 0, lineHeight: 1, width: '28px', height: '28px', borderRadius: '5px' }}
              title={`파일 첨부 (최대 ${MAX_FILES}개 · 이미지 5MB · 문서 20MB)`}>
              <IconPaperclip size={18} stroke={1.8} aria-hidden="true" />
            </button>
            <input ref={fileRef} type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
            {models.length > 0 && (
              <select value={selectedChatModel} onChange={(e) => setSelectedChatModel(e.target.value)}
                title="사용할 모델 선택"
                style={{ flex: mobile ? (narrow ? '1 1 88px' : '1 1 150px') : '0 0 auto', minWidth: mobile ? (narrow ? '88px' : '132px') : undefined, border: 'none', padding: '4px', fontSize: mobile ? '10px' : '11px', color: 'var(--gw-secondary, #64635f)', background: 'transparent', cursor: 'pointer', outline: 'none', maxWidth: mobile ? '100%' : '150px', height: '32px' }}>
                {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            )}
            {!ultraNarrow && (
            <button type="button" onClick={() => setWebSearch((v) => !v)}
              title="웹 검색 (실시간 정보 조회 · 검색 1건당 추가 요금)"
              style={pillButtonStyle(webSearch)}>
              <IconWorld size={15} stroke={1.8} aria-hidden="true" />{narrow ? '' : '웹 검색'}
              {!narrow && (
                <span style={{ fontWeight: '700', padding: '0 5px', borderRadius: '8px', fontSize: '11px',
                  background: webSearch ? 'rgba(255,255,255,.25)' : 'var(--accent-weak)',
                  color: webSearch ? 'white' : 'var(--selected-text)' }}>
                  {webSearch ? 'ON' : 'OFF'}
                </span>
              )}
            </button>
            )}
            {!ultraNarrow && (
            <button type="button" onClick={() => setReasoning((v) => !v)} disabled={!reasoningAvailable}
              title={reasoningAvailable ? '심층 추론 — 더 오래 생각한 뒤 답합니다' : '이 모델은 심층 추론을 지원하지 않습니다'}
              style={{ ...pillButtonStyle(reasoningActive), cursor: reasoningAvailable ? 'pointer' : 'not-allowed', opacity: reasoningAvailable ? 1 : 0.55 }}>
              <IconBrain size={15} stroke={1.8} aria-hidden="true" />{narrow ? '' : '심층'}
              <span style={{ display: narrow ? 'none' : undefined, fontWeight: '700', padding: '0 5px', borderRadius: '8px', fontSize: '11px',
                background: reasoningActive ? 'rgba(255,255,255,.25)' : 'var(--accent-weak)',
                color: reasoningActive ? 'white' : 'var(--selected-text)' }}>
                {reasoningActive ? 'ON' : 'OFF'}
              </span>
            </button>
            )}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button ref={toolsButtonRef} type="button" className="chat-icon-btn" onClick={() => setToolPopoverOpen((v) => !v)}
                aria-haspopup="dialog" aria-expanded={toolPopoverOpen} aria-label="도구"
                title="도구"
                style={{ width: '28px', height: '28px', borderRadius: '5px', border: toolPopoverOpen ? '1px solid var(--accent)' : '1px solid var(--border)', background: toolPopoverOpen ? 'var(--accent)' : undefined, color: toolPopoverOpen ? 'white' : undefined, cursor: 'pointer', fontSize: '18px', lineHeight: 1, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                +
              </button>
              {toolPopoverOpen && (
                <div ref={toolsPopoverRef} role="dialog" aria-label="도구 선택"
                  style={{ position: mobile ? 'fixed' : 'absolute', left: mobile ? '12px' : 0, right: mobile ? '12px' : undefined, bottom: mobile ? (keyboardOpen ? '76px' : 'calc(88px + env(safe-area-inset-bottom))') : '38px', width: mobile ? 'auto' : '320px', background: 'white', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 16px 36px rgba(15,23,42,.16)', padding: '10px', zIndex: 80 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 2px 8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#111827' }}>도구</span>
                    <button type="button" onClick={closeTools} aria-label="도구 닫기"
                      style={{ border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 4px' }}>
                      ×
                    </button>
                  </div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {/* 확대 배율을 크게 올리면 컨트롤 바에 자리가 없다 — 그때만 이 둘을 여기로 옮긴다.
                        (배율 상한은 그대로 둔다. 기기 크기에 따라 크게 볼 필요가 있다 — 마스터 2026-08-21) */}
                    {ultraNarrow && renderToolRow({
                      key: 'websearch',
                      icon: <IconWorld size={15} stroke={1.8} aria-hidden="true" />,
                      label: '웹 검색',
                      description: '실시간 정보를 찾아봅니다. 검색 1건마다 요금이 더해집니다.',
                      active: webSearch,
                      status: webSearch ? 'ON' : 'OFF',
                      onClick: () => setWebSearch((v) => !v),
                    })}
                    {ultraNarrow && renderToolRow({
                      key: 'reasoning',
                      icon: <IconBrain size={15} stroke={1.8} aria-hidden="true" />,
                      label: '심층 추론',
                      description: reasoningAvailable ? '더 오래 생각한 뒤 답합니다.' : '이 모델은 심층 추론을 지원하지 않습니다.',
                      active: reasoningActive,
                      disabled: !reasoningAvailable,
                      status: reasoningActive ? 'ON' : 'OFF',
                      onClick: () => setReasoning((v) => !v),
                    })}
                    {renderToolRow({
                      key: 'internal',
                      icon: <IconBuilding size={15} stroke={1.8} aria-hidden="true" />,
                      label: '사내데이터',
                      description: dbAccess ? (internalLan ? '출고·재고·주문 등 사내 데이터를 조회합니다.' : '사내망에서만 사용할 수 있습니다.') : '사내데이터 권한이 필요합니다.',
                      active: internalData,
                      disabled: dbAccess && !internalLan,
                      status: dbAccess ? (internalLan ? (internalData ? 'ON' : 'OFF') : '사내망 필요') : <><IconLock size={11} stroke={1.8} aria-hidden="true" />요청하기</>,
                      onClick: dbAccess ? () => setInternalData((v) => !v) : requestPermission,
                    })}
                    {renderToolRow({
                      key: 'rag',
                      icon: <IconBook2 size={15} stroke={1.8} aria-hidden="true" />,
                      label: '문서 기반 답변',
                      description: ragAccess ? '승인된 학습자료를 근거로 답변하고 출처를 표시합니다.' : '관리자에게 RAG 사용 권한을 요청하세요',
                      active: ragAccess && useRag,
                      disabled: !ragAccess,
                      status: ragAccess ? (useRag ? 'ON' : 'OFF') : <><IconLock size={11} stroke={1.8} aria-hidden="true" />권한 필요</>,
                      onClick: ragAccess ? () => setUseRag((v) => !v) : undefined,
                    })}
                    {renderToolRow({
                      key: 'drive',
                      icon: <IconFolder size={15} stroke={1.8} aria-hidden="true" />,
                      label: '드라이브 첨부',
                      description: driveAccess ? '드라이브에서 파일을 선택합니다.' : '드라이브 권한이 필요합니다.',
                      disabled: driveAccess && pendingFiles.length >= MAX_FILES,
                      status: driveAccess ? (pendingFiles.length >= MAX_FILES ? '파일 제한' : '열기') : <><IconLock size={11} stroke={1.8} aria-hidden="true" />요청하기</>,
                      onClick: driveAccess ? openDrivePicker : requestPermission,
                    })}
                    {renderToolRow({
                      key: 'prompts',
                      icon: <IconFileText size={15} stroke={1.8} aria-hidden="true" />,
                      label: '프롬프트',
                      description: '저장된 업무 프롬프트를 삽입합니다.',
                      status: '열기',
                      onClick: openPromptLibrary,
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* 전송은 앞 토글들과 같은 줄 오른쪽 끝에 붙인다. 모바일에서 flex:'1 1 100%' 로 한 줄을 통째로
                쓰게 했더니(잘림 방지 의도) 확대 배율에서 컨트롤이 4줄이 되고, 카드가 하단 여백을 넘어
                전송 버튼이 탭바에 가려 눌리지 않았다(2026-08-21 실측: 기본 150%에서 88% 가림·클릭 불가). */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', justifyContent: 'flex-end', flex: '0 0 auto' }}>
              {loading && (
                <button onClick={stopResponse} title="응답 생성을 중지합니다"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '50%', padding: 0, cursor: 'pointer', fontWeight: '600', fontSize: '14px', flex: '0 0 auto', width: '38px', height: '38px' }}>
                  <span style={{ width: '10px', height: '10px', background: '#ef4444', borderRadius: '2px', display: 'inline-block' }} />
                </button>
              )}
              <button onClick={handleSend} disabled={!canSend}
                title="전송"
                style={{ background: canSend ? 'var(--accent)' : 'var(--gw-gray-soft, #f1f1ef)', color: canSend ? 'white' : 'var(--gw-muted, #92918d)', border: 'none', borderRadius: '50%', padding: 0, cursor: canSend ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '16px', flex: '0 0 auto', width: '32px', height: '32px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ↑
              </button>
            </div>
          </div>
        </div>
        <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconPaperclip size={13} stroke={1.8} aria-hidden="true" />최대 {MAX_FILES}개 · 이미지 5MB · 문서 20MB</span>
            {pendingFiles.length > 0 && (
              <span style={{ color: pendingFiles.length >= MAX_FILES ? '#ef4444' : 'var(--text-muted)', marginLeft: '6px' }}>
                ({pendingFiles.length}/{MAX_FILES})
              </span>
            )}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: mobile ? 'left' : 'right' }}>AI 응답은 부정확할 수 있습니다. 중요한 내용은 반드시 확인하세요.</span>
        </div>
        </div>
      </div>
    </>
  );
}
